import React, { useEffect, useRef, useState } from 'react';
import { Avatar, Ic, fmtNum } from './ui';

/**
 * Bot değiştirici — tek düğme + açılır liste.
 * Masaüstünde düğmenin altında açılır, mobilde alttan sayfa olarak açılır.
 * { bots, selected, onSelect, unread: { [botId]: sayı }, block: tam genişlik düğme }
 */
const CSS = `
.bsw { position:relative; min-width:0; }
.bsw-btn { display:flex; align-items:center; gap:10px; width:100%; min-width:0; background:var(--surface3); border:1px solid transparent; border-radius:12px; padding:5px 10px 5px 5px; color:var(--text); font:inherit; cursor:pointer; text-align:left; transition:background .12s, border-color .12s; -webkit-tap-highlight-color:transparent; }
.bsw-btn:hover, .bsw-btn.open { border-color:var(--border); background:var(--hover); }
.bsw-btn .nm { font-weight:600; font-size:14.5px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.bsw-btn .sb { font-size:12px; color:var(--text3); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.bsw-dot { background:var(--accent); color:#fff; font-size:11.5px; font-weight:700; border-radius:10px; min-width:20px; height:20px; line-height:20px; text-align:center; padding:0 6px; flex-shrink:0; }
.bsw-back { position:fixed; inset:0; z-index:2400; }
.bsw-pop { position:absolute; top:calc(100% + 6px); left:0; width:min(340px, calc(100vw - 24px)); background:#1f2936; border-radius:12px; box-shadow:var(--shadow-lg); z-index:2401; overflow:hidden; animation:modalIn .14s ease-out; display:flex; flex-direction:column; max-height:min(460px, 70vh); }
.bsw-row { display:flex; align-items:center; gap:12px; padding:8px 12px; cursor:pointer; transition:background .1s; }
.bsw-row:hover { background:rgba(255,255,255,.05); }
.bsw-row.on { background:rgba(82,136,193,.18); }
.bsw-av { position:relative; flex-shrink:0; }
.bsw-av .bdg { position:absolute; right:-4px; top:-4px; background:var(--accent); color:#fff; font-size:11px; font-weight:700; border-radius:10px; min-width:19px; height:19px; line-height:19px; text-align:center; padding:0 5px; box-shadow:0 0 0 2px #1f2936; }
@media (max-width: 768px) {
  .bsw-back { background:rgba(0,0,0,.5); animation:fadeIn .15s ease; }
  .bsw-pop { position:fixed; top:auto; left:0; right:0; bottom:0; width:100%; border-radius:16px 16px 0 0; max-height:78vh; animation:sheetIn .22s ease-out; padding-bottom:env(safe-area-inset-bottom); background:var(--surface); }
  .bsw-row { padding:10px 16px; }
  .bsw-grab { display:block !important; }
}
`;

export function BotList({ bots, selected, onPick, unread = {}, autoFocus }) {
  const [q, setQ] = useState('');
  const list = q ? bots.filter(b => (b.name || '').toLocaleLowerCase('tr').includes(q.toLocaleLowerCase('tr'))) : bots;
  return (
    <>
      {bots.length > 6 && (
        <div style={{ padding: '10px 12px 6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface3)', borderRadius: 20, padding: '0 12px', height: 38 }}>
            <Ic n="search" s={17} c="var(--text3)" />
            <input autoFocus={autoFocus} value={q} onChange={e => setQ(e.target.value)} placeholder="Bot ara"
              style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: 'var(--text)', fontSize: 15, minWidth: 0, fontFamily: 'inherit' }} />
          </div>
        </div>
      )}
      <div style={{ overflowY: 'auto', padding: '4px 0 6px', flex: 1 }}>
        {list.map(b => {
          const on = selected?.id === b.id;
          const u = unread[b.id] || 0;
          return (
            <div key={b.id} className={'bsw-row' + (on ? ' on' : '')} onClick={() => onPick(b)}>
              <div className="bsw-av">
                <Avatar id={b.id} name={b.name} size={40}>{b.bot_type === 'date' ? <Ic n="heart" s={19} /> : null}</Avatar>
                {u > 0 && !on && <span className="bdg">{u > 99 ? '99+' : u}</span>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="truncate" style={{ fontWeight: 600, fontSize: 15 }}>{b.name}</div>
                <div className="truncate" style={{ fontSize: 12.5, color: 'var(--text3)' }}>
                  {b.is_active === false ? 'Pasif · ' : ''}{b.user_count != null ? fmtNum(b.user_count) + ' kullanıcı' : (b.username ? '@' + b.username : '')}
                </div>
              </div>
              {on && <Ic n="check" s={20} c="var(--link)" sw={2.4} />}
            </div>
          );
        })}
        {!list.length && <div style={{ padding: 18, textAlign: 'center', color: 'var(--text3)', fontSize: 14 }}>Bot bulunamadı</div>}
      </div>
    </>
  );
}

export default function BotSwitcher({ bots = [], selected, onSelect, unread = {}, style, sub }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const k = e => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [open]);
  if (!bots.length) return null;
  const cur = bots.find(b => b.id === selected?.id) || selected;
  const others = Object.entries(unread).reduce((s, [id, n]) => (String(id) === String(cur?.id) ? s : s + (n || 0)), 0);
  return (
    <div className="bsw" ref={wrap} style={style}>
      <style>{CSS}</style>
      <button type="button" className={'bsw-btn' + (open ? ' open' : '')} onClick={() => setOpen(v => !v)} title="Bot değiştir">
        {cur ? <Avatar id={cur.id} name={cur.name} size={32}>{cur.bot_type === 'date' ? <Ic n="heart" s={16} /> : null}</Avatar> : <div className="ic-circle" style={{ width: 32, height: 32, background: 'var(--accent-s)' }}><Ic n="bot" s={18} c="var(--link)" /></div>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="nm">{cur?.name || 'Bot seçin'}</div>
          <div className="sb">{sub != null ? sub : `${bots.length} bot · değiştirmek için dokunun`}</div>
        </div>
        {others > 0 && <span className="bsw-dot" title="Diğer botlarda okunmamış">{others > 99 ? '99+' : others}</span>}
        <Ic n="down" s={18} c="var(--text3)" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
      </button>
      {open && (
        <>
          <div className="bsw-back" onClick={() => setOpen(false)} />
          <div className="bsw-pop">
            <div className="bsw-grab" style={{ display: 'none', width: 36, height: 4, borderRadius: 2, background: 'var(--border2)', margin: '8px auto 2px' }} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px 4px' }}>
              <span style={{ fontWeight: 600, fontSize: 15 }}>Bot seçin</span>
              <span style={{ fontSize: 12.5, color: 'var(--text3)' }}>{bots.length} bot</span>
            </div>
            <BotList bots={bots} selected={cur} unread={unread} autoFocus={window.innerWidth > 768} onPick={b => { setOpen(false); if (b.id !== cur?.id) onSelect(b); }} />
          </div>
        </>
      )}
    </div>
  );
}
